/**
 * NewMessageModal
 * User search + compose flow for starting a new conversation.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { searchUsers, startConversation, UserSearchResult } from '../services/messagingService';
import ModalCloseButton from './ModalCloseButton';

interface NewMessageModalProps {
  onClose: () => void;
  onMessageSent: (conversationId: number) => void;
}

const NewMessageModal: React.FC<NewMessageModalProps> = ({ onClose, onMessageSent }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserSearchResult | null>(null);
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const messageInputRef = useRef<HTMLTextAreaElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Focus search input on mount
  useEffect(() => {
    setTimeout(() => searchInputRef.current?.focus(), 100);
  }, []);

  // Focus message input when user is selected
  useEffect(() => {
    if (selectedUser) {
      setTimeout(() => messageInputRef.current?.focus(), 100);
    }
  }, [selectedUser]);

  // Debounced user search
  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
    setError(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      const results = await searchUsers(query);
      setSearchResults(results);
      setSearching(false);
    }, 300);
  }, []);

  const handleSelectUser = (user: UserSearchResult) => {
    setSelectedUser(user);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleSend = async () => {
    if (!selectedUser || !messageText.trim()) return;
    setSending(true);
    setError(null);
    const res = await startConversation({
      recipient_id: selectedUser.id,
      message: messageText.trim(),
    });
    setSending(false);
    if (res.success && res.conversation_id) {
      onMessageSent(res.conversation_id);
    } else {
      setError(res.error || 'Failed to send message');
    }
  };

  const modal = (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        justifyContent: 'flex-start',
        paddingTop: '10vh',
        zIndex: 20001,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--color-bg-primary)',
          width: '100%',
          maxHeight: '70vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          flexShrink: 0,
          flexGrow: 0,
          textAlign: 'left' as const,
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '16px',
            borderBottom: '1px solid var(--color-border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '600' }}>New Message</h3>
          <ModalCloseButton onClick={onClose} />
        </div>

        {/* Recipient selection */}
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-border)' }}>
          {selectedUser ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '14px', color: 'var(--color-text-muted)' }}>To:</span>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: 'var(--color-bg-secondary)',
                  borderRadius: '16px',
                  padding: '4px 10px',
                  fontSize: '14px',
                }}
              >
                <span style={{ fontWeight: '500' }}>{selectedUser.display_name}</span>
                <button
                  onClick={() => setSelectedUser(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0 2px',
                    color: 'var(--color-text-muted)',
                    fontSize: '16px',
                    lineHeight: 1,
                  }}
                >
                  ×
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '14px', color: 'var(--color-text-muted)' }}>To:</span>
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder="Search by name or email…"
                  style={{
                    flex: 1,
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    fontSize: '14px',
                    color: 'var(--color-text-primary)',
                    padding: '4px 0',
                  }}
                />
              </div>
              {/* Search results */}
              {(searching || searchResults.length > 0) && (
                <div style={{ marginTop: '8px', maxHeight: '160px', overflow: 'auto' }}>
                  {searching && searchResults.length === 0 && (
                    <div style={{ padding: '8px 0', fontSize: '13px', color: 'var(--color-text-muted)' }}>
                      Searching…
                    </div>
                  )}
                  {searchResults.map((u) => (
                    <div
                      key={u.id}
                      onClick={() => handleSelectUser(u)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '8px 4px',
                        cursor: 'pointer',
                        borderRadius: '8px',
                      }}
                    >
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          background: 'var(--color-accent)',
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: '600',
                          fontSize: '14px',
                          flexShrink: 0,
                        }}
                      >
                        {(u.display_name || '?')[0].toUpperCase()}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: '14px', fontWeight: '500' }}>{u.display_name}</div>
                        <div style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>{u.email}</div>
                      </div>
                    </div>
                  ))}
                  {!searching && searchQuery.trim() && searchResults.length === 0 && (
                    <div style={{ padding: '8px 0', fontSize: '13px', color: 'var(--color-text-muted)' }}>
                      No users found
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Message input */}
        {selectedUser && (
          <div style={{ flex: 1, padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <textarea
              ref={messageInputRef}
              value={messageText}
              onChange={(e) => {
                setMessageText(e.target.value);
                const ta = e.target;
                ta.style.height = 'auto';
                ta.style.height = Math.min(ta.scrollHeight, 200) + 'px';
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="Type your message…"
              rows={3}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '12px',
                border: '1px solid var(--color-border)',
                background: 'var(--color-bg-secondary)',
                color: 'var(--color-text-primary)',
                fontSize: '14px',
                lineHeight: '1.4',
                outline: 'none',
                resize: 'none',
                fontFamily: 'inherit',
                maxHeight: '200px',
                overflow: 'auto',
                boxSizing: 'border-box',
                textAlign: 'left' as const,
              }}
            />
            {error && (
              <div style={{ fontSize: '13px', color: 'var(--color-error, #e74c3c)' }}>{error}</div>
            )}
            <button
              onClick={handleSend}
              disabled={sending || !messageText.trim()}
              style={{
                padding: '10px 20px',
                borderRadius: '10px',
                border: 'none',
                background: messageText.trim() ? 'var(--color-accent)' : 'var(--color-bg-tertiary)',
                color: messageText.trim() ? '#fff' : 'var(--color-text-muted)',
                fontSize: '15px',
                fontWeight: '600',
                cursor: messageText.trim() ? 'pointer' : 'default',
                alignSelf: 'flex-end',
              }}
            >
              {sending ? 'Sending…' : 'Send'}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default NewMessageModal;
